import { defineStore } from 'pinia'
import { useUserStore } from './userStore'
import { useAuthStore } from './authStore'
import axios from '../axios.js'

export const useGlobalPermissionsStore = defineStore('globalPermissions', {
  state: () => ({
    // User permissions cache - userId -> permissions array
    userPermissionsCache: new Map(),
    
    // Available permissions in the system (fetched from API)
    availablePermissions: {},
    
    // Loading states
    loading: false,
    permissionsLoaded: false,
    
    // Cache settings
    lastFetch: null,
    cacheTimeout: 0, // No cache - always fetch fresh
    
    // Current user we're checking permissions for
    currentCheckingUserId: null,
  }),

  getters: {
    // Check if permissions are cached and still valid
    isCacheValid: (state) => {
      if (!state.lastFetch) return false
      return (Date.now() - state.lastFetch) < state.cacheTimeout
    },

    // Get current user from userStore
    currentUser: () => {
      const userStore = useUserStore()
      return userStore.currentUser
    },

    // Get current role (considering impersonation)
    currentRole: () => {
      const userStore = useUserStore()
      const authStore = useAuthStore()
      
      if (authStore.isImpersonating) {
        return authStore.currentRole
      }
      
      return userStore.currentUser?.role
    },

    // Get permissions for specific user
    getUserPermissions: (state) => (userId) => {
      return state.userPermissionsCache.get(userId?.toString()) || []
    },

    // Get current user's permissions
    currentUserPermissions: (state) => {
      const userStore = useUserStore()
      const userId = userStore.currentUser?.id
      return userId ? state.userPermissionsCache.get(userId.toString()) || [] : []
    }
  },

  actions: {
    // Fetch permissions for specific user
    async fetchUserPermissions(userId, forceRefresh = false) {
      const userIdStr = userId?.toString()
      if (!userIdStr) {
        console.warn('No user ID provided for permission check')
        return []
      }

      // Use cache if valid and not forcing refresh
      if (!forceRefresh && this.userPermissionsCache.has(userIdStr) && this.isCacheValid) {
        return this.userPermissionsCache.get(userIdStr)
      }

      this.loading = true
      this.currentCheckingUserId = userIdStr
      
      try {
        // console.log(`Fetching permissions for user ${userId}`)
        
        // Fetch user's specific permissions from API
        const response = await axios.get(`/api/users/${userId}/permissions`)
        
        const permissions = response.data.permissions || []
        
        // Cache the permissions
        this.userPermissionsCache.set(userIdStr, permissions)
        this.permissionsLoaded = true
        this.lastFetch = Date.now()
        
        // console.log(`Loaded permissions for user ${userId}:`, permissions)
        
        return permissions
      } catch (error) {
        console.error(`Failed to fetch permissions for user ${userId}:`, error)
        
        // Fallback to empty permissions on error
        this.userPermissionsCache.set(userIdStr, [])
        return []
      } finally {
        this.loading = false
        this.currentCheckingUserId = null
      }
    },

    // Fetch current user's permissions
    async fetchCurrentUserPermissions(forceRefresh = false) {
      const userStore = useUserStore()
      const userId = userStore.currentUser?.id
      
      if (!userId) {
        console.warn('No current user found')
        return []
      }

      return await this.fetchUserPermissions(userId, forceRefresh)
    },

    // Fetch all available permissions in the system (for admin use)
    async fetchAvailablePermissions() {
      try {
        const response = await axios.get('/api/permissions')
        this.availablePermissions = response.data.permissions || {}
        return this.availablePermissions
      } catch (error) {
        console.error('Failed to fetch available permissions:', error)
        return {}
      }
    },

    // Check if specific user has permission
    hasPermission(userId, permission) {
      const userIdStr = userId?.toString()
      if (!userIdStr) return false

      const permissions = this.userPermissionsCache.get(userIdStr) || []
      
      // Admin wildcard check
      if (permissions.includes('*')) {
        return true
      }

      // Direct permission check
      return permissions.includes(permission)
    },

    // Check if current user has permission
    currentUserHasPermission(permission) {
      const userStore = useUserStore()
      const userId = userStore.currentUser?.id
      return userId ? this.hasPermission(userId, permission) : false
    },

    // Check multiple permissions for user (any)
    hasAnyPermission(userId, permissions) {
      if (!permissions || permissions.length === 0) return true
      
      const userIdStr = userId?.toString()
      if (!userIdStr) return false

      const userPermissions = this.userPermissionsCache.get(userIdStr) || []
      
      if (userPermissions.includes('*')) {
        return true
      }

      return permissions.some(permission => userPermissions.includes(permission))
    },

    // Check multiple permissions for user (all)
    hasAllPermissions(userId, permissions) {
      if (!permissions || permissions.length === 0) return true
      
      const userIdStr = userId?.toString()
      if (!userIdStr) return false

      const userPermissions = this.userPermissionsCache.get(userIdStr) || []
      
      if (userPermissions.includes('*')) {
        return true
      }

      return permissions.every(permission => userPermissions.includes(permission))
    },

    // Current user permission checks
    currentUserHasAnyPermission(permissions) {
      const userStore = useUserStore()
      const userId = userStore.currentUser?.id
      return userId ? this.hasAnyPermission(userId, permissions) : false
    },

    currentUserHasAllPermissions(permissions) {
      const userStore = useUserStore()
      const userId = userStore.currentUser?.id
      return userId ? this.hasAllPermissions(userId, permissions) : false
    },

    // Note: Role-based checks removed - using permissions only

    // Convenience methods for current user - based ONLY on permissions
    isAdmin() {
      return this.currentUserHasPermission('admin.access') || this.currentUserHasPermission('*')
    },

    isTrader() {
      return this.currentUserHasAnyPermission(['trading.basic', 'trading.advanced'])
    },

    isClient() {
      return this.currentUserHasAnyPermission(['fixtures.view', 'reports.basic']) && !this.isAdmin() && !this.isTrader()
    },

    // Component access helpers
    canAccessComponent(userId, componentName, requiredPermissions = []) {
      if (!requiredPermissions || requiredPermissions.length === 0) {
        return true
      }
      
      return this.hasAnyPermission(userId, requiredPermissions)
    },

    // Current user component access
    currentUserCanAccessComponent(componentName, requiredPermissions = []) {
      const userStore = useUserStore()
      const userId = userStore.currentUser?.id
      return userId ? this.canAccessComponent(userId, componentName, requiredPermissions) : false
    },

    // Clear permissions cache
    clearPermissionsCache(userId = null) {
      if (userId) {
        this.userPermissionsCache.delete(userId.toString())
      } else {
        this.userPermissionsCache.clear()
      }
      this.permissionsLoaded = false
      this.lastFetch = null
    },

    // Refresh permissions for user
    async refreshUserPermissions(userId) {
      this.clearPermissionsCache(userId)
      return await this.fetchUserPermissions(userId, true)
    },

    // Debug helper (development only)
    debugPermissions(userId = null) {
      if (process.env.NODE_ENV === 'development') {
        const targetUserId = userId || this.currentUser?.id
        const permissions = this.getUserPermissions(targetUserId)
        console.log(`Debug permissions for user ${targetUserId}:`, permissions)
        return permissions
      }
    }
  }
}) 